import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import ZeroResults from '@/components/ZeroResults/ZeroResults';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchSavedSearchesIfNeeded } from '@/redux/modules/savedSearch';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import {
    getSoldItemImportantCategories,
    getSoldItemImportantCreators,
    getSoldItemImportantOrigins,
    getSoldItemImportantStylePeriod,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions';
import { CategoryLink } from '@/types/CategoryLink';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { Suggestions } from '@/types/SimilarItemInfo';
import { getCategoryPageUrl, getCombinedCategoryPageUrl, urlSafeTitle } from '@/utils/urls';

const safeNameUrl = (unsafeName: string = '') => {
    const cleanedName = unsafeName.toLowerCase().replace('&', '');
    return urlSafeTitle(cleanedName);
};

export type ZeroResultsContainerCategory = {
    count: number;
    id: string | number;
    level: number;
    name: string;
};

const mapStateToProps = (state) => {
    const { soldItemIds = [], totalSoldItems: soldItemsFound } = getSearchUiState(state);
    const { houses, suggestions } = getSecondarySearchSuggestions(state);

    const categoryLinks: { key: string; link: string; title: string }[] = [];
    const creators = getSoldItemImportantCreators(state);
    const categories = getSoldItemImportantCategories(state);
    const origins = getSoldItemImportantOrigins(state);
    const stylesPeriods = getSoldItemImportantStylePeriod(state);

    const searches: Array<number[]> = [];

    creators.forEach((obj) => {
        const { id, name: unsafeName } = obj;
        const name = urlSafeTitle(unsafeName);
        categoryLinks.push({
            key: name,
            link: getCategoryPageUrl(name, id),
            title: unsafeName,
        });
    });

    const L1 = categories.find((cat: ZeroResultsContainerCategory) => cat.level === 1);
    const style = stylesPeriods[0];
    if (L1 && style) {
        searches.push([Number(L1.id), Number(style.id)]);
        const link = getCombinedCategoryPageUrl(L1, style);
        const title = `${style.name} ${L1.name}`;
        categoryLinks.push({
            key: urlSafeTitle(title),
            link,
            title,
        });
    }

    const origin = origins[0];
    if (L1 && origin) {
        const link = getCombinedCategoryPageUrl(L1, origin);
        const title = `${origin.name} ${L1.name}`;
        categoryLinks.push({
            key: urlSafeTitle(title),
            link,
            title,
        });
    }

    categories
        .filter((cat) => cat.level > 1)
        .forEach((obj) => {
            const { id, name: unsafeName = '' } = obj;
            const name = safeNameUrl(unsafeName);
            categoryLinks.push({
                key: name,
                link: getCategoryPageUrl(name, `${id}`),
                title: unsafeName,
            });
        });

    const isLoggedIn = isUserLoggedIn(state);

    return {
        categoryLinks,
        houses,
        isLoggedIn,
        soldItemIds,
        soldItemsFound,
        suggestions,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchSavedSearchesIfNeeded,
        },
        dispatch
    );

type Props = {
    allSearchParams: SearchParamsType;
    categoryLinks: CategoryLink[];
    categoryPage?: boolean;
    className?: string;
    creator?: any;
    fetchSavedSearchesIfNeeded: () => void;
    houses: any[];
    isLoggedIn: boolean;
    saveSearchParams: any;
    searchTerm: string;
    seeResultsRoute?: string;
    soldItemIds?: number[];
    soldItemsFound?: number;
    suggestions: Suggestions[];
};

class ZeroResultsContainer extends Component<Props> {
    static defaultProps = {
        searchTerm: '',
    };

    componentDidMount() {
        const { isLoggedIn } = this.props;

        if (isLoggedIn) {
            this.props.fetchSavedSearchesIfNeeded();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { isLoggedIn } = this.props;

        if (!prevProps.isLoggedIn && isLoggedIn) {
            this.props.fetchSavedSearchesIfNeeded();
        }
    }

    render() {
        return <ZeroResults {...this.props} />;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ZeroResultsContainer);
